import React from 'react'
import { Button } from 'antd'
import { Table } from './Table'
import { v4 as uuid } from 'uuid'
import { t } from '../../../config/i18n'
import { ColumnsType } from 'antd/es/table'
import styles from './CatalogBindings.module.scss'
import { TCatalogBindingItem } from '../../../models/CatalogBindingItem'
import { TAbstractCatalogItem } from '../../../models/AbstractCatalogItem'
import { TAbstractCatalogConfig } from '../../../models/AbstractCatalogConfig'
import { CatalogBindingsSelectEntity } from './catalogBindings/SelectEntity'
import { modalSelectBusiness, modalSelectResource, modalSelectTaxonomy } from '../../../lib/utilsUI'
import cn from 'classnames'

interface Props {
  catalogConfig: TAbstractCatalogConfig
  catalogItem: TAbstractCatalogItem
  bindings: TCatalogBindingItem[]
  onChange: (bindings: TCatalogBindingItem[]) => void
}

export const CatalogBindings = ({ catalogConfig, catalogItem, bindings, onChange }: Props) => {
  const [taxBusinessId, setTaxBusinessId] = React.useState<string>()

  const onSelectEntity = React.useCallback(async (entity: string) => {
    const businessItem = await modalSelectBusiness()
    if (entity === 'business') {
      const bindingItem: TCatalogBindingItem = {
        key: uuid(),
        businessId: businessItem.id,
        catalogId: catalogConfig.id,
        catalogItemId: catalogItem._id,
        entityType: entity,
        entityId: businessItem.id,
        data: {
          businessName: businessItem.general_info.name,
        }
      }
      onChange([...bindings, bindingItem])
    }
    if (entity === 'taxonomy') {
      const taxonomyItem = await modalSelectTaxonomy(businessItem.id)
      const bindingItem: TCatalogBindingItem = {
        key: uuid(),
        businessId: businessItem.id,
        catalogId: catalogConfig.id,
        catalogItemId: catalogItem._id,
        entityType: entity,
        entityId: taxonomyItem.id,
        data: {
          businessName: businessItem.general_info.name,
          entityName: taxonomyItem.name,
        }
      }
      onChange([...bindings, bindingItem])
    }

    if (entity === 'resource') {
      const resourceItem = await modalSelectResource(businessItem.id)
      const bindingItem: TCatalogBindingItem = {
        key: uuid(),
        businessId: businessItem.id,
        catalogId: catalogConfig.id,
        catalogItemId: catalogItem._id,
        entityType: entity,
        entityId: resourceItem.id,
        data: {
          businessName: businessItem.general_info.name,
          entityName: `${resourceItem.name} ${resourceItem.surname}`,
        }
      }
      onChange([...bindings, bindingItem])
    }
  }, [onChange, bindings, catalogConfig, catalogItem])

  const onDelete = React.useCallback((item: TCatalogBindingItem) => {
    onChange(bindings.filter(i => i !== item))
  }, [bindings, onChange])

  const entityList = (catalogConfig as TObjectAny).entityList || ['taxonomy']
  const columns = useColumns(onDelete)
  const sortedBindings = [...bindings].sort((a, b) => {
    const bid = a.businessId.localeCompare(b.businessId)
    return bid || a.entityType.localeCompare(b.entityType)
  })

  return (
    <div className={styles.root}>
      <CatalogBindingsSelectEntity
        entityList={entityList}
        onSelect={onSelectEntity}
        className={'mb4'}
      />

      {bindings.length > 0 && (
        <Table
          className={styles.table}
          idField={'key'}
          items={sortedBindings}
          columns={columns}
        />
      )}
    </div>
  )
}

const useColumns = (
  onDelete: (item: TCatalogBindingItem) => void,
): ColumnsType<TCatalogBindingItem> => {

  return React.useMemo((): ColumnsType<TCatalogBindingItem> => [
    {
      key: 'business',
      title: t.catalog.businessTitle,
      width: '50%',
      render: (_, item) => (
        <div>
          <div className={styles.id}>{item.businessId}</div>
          <div>{item.data.businessName}</div>
        </div>
      ),
    },
    {
      key: 'entity',
      title: t.catalog.entityTitle,
      width: '50%',
      render: (_, item) => item.entityType === 'business' ? '-' : (
        <div>
          <div className={styles.id}>{item.entityType} {item.entityId}</div>
          <div>
            {item.data.entityName}
            {item.entityType === 'taxonomy' && (
              <span className={cn(styles.id, 'ml1')}>
                (
                {item.data.taxonomyDuration && <span>{item.data.taxonomyDuration} min. </span>}
                {item.data.taxonomyPrice && <span>{item.data.taxonomyPrice.amount} {item.data.taxonomyPrice.currency}</span>}
                )
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      width: '1%',
      render: (_, item) => (
        <Button type={'link'} onClick={() => onDelete(item)}>{t.common.delete}</Button>
      ),
    },
  ], [onDelete])
}
